import { last } from 'lodash/fp'
import type { DataRouter, Params, RouteObject, RouterState } from 'react-router'
import { isLogRocketEnabled, onLogRocketSessionReset, trackLogRocketEvent } from './logRocket'

/**
 * SPA page tracking for LogRocket (SYN-8473).
 *
 * LogRocket already records every History API navigation, but under the raw
 * URL, so `/members/123` and `/members/456` count as different pages. Each
 * navigation also fires a `PageViewed` custom event whose `route` property is
 * the matched route pattern (`/members/:tenantMemberId`), which LogRocket
 * analytics can group, filter and funnel by. Tab-like params are filled in
 * with their value instead (`/resource-hub/videos`), so each tab counts as its
 * own page. The raw (PII-sanitized) URL is still recorded alongside it for
 * debugging.
 */

export const PAGE_VIEWED_EVENT = 'PageViewed'

/**
 * Params whose values are a small fixed set of sub-pages (each is typed as an
 * enum in `AppPaths`), so they are kept in the reported route rather than
 * normalized. Never add an ID-like param here: every value would become a
 * separate "page" and could carry identifying data.
 */
const PAGE_PARAMS = ['tab', 'subTab', 'type', 'category']

type RouteMatchLike = { route: Pick<RouteObject, 'path'>; params?: Params }

const DYNAMIC_SEGMENT = /^:(\w+)(\?)?$/

/** Fills in {@link PAGE_PARAMS}, dropping an optional one that was not provided. */
const expandPageParams = (pattern: string, params: Params): string =>
  pattern
    .split('/')
    .flatMap(segment => {
      const [, name, optional] = segment.match(DYNAMIC_SEGMENT) ?? []
      if (!name || !PAGE_PARAMS.includes(name)) return [segment]
      const value = params[name]
      if (value) return [value]
      return optional ? [] : [segment]
    })
    .join('/')

/**
 * Builds the route reported to LogRocket, e.g. `/members/:tenantMemberId` or
 * `/resource-hub/videos`, by joining the `path` of each matched route.
 * Pathless layout and index routes contribute nothing, and an absolute child
 * path replaces the accumulated prefix (React Router requires absolute child
 * paths to already include their parent's).
 */
export const getRoutePattern = (matches: readonly RouteMatchLike[]): string => {
  const pattern = matches.reduce((acc, { route: { path } }) => {
    if (!path) return acc
    if (path.startsWith('/')) return path
    return `${acc.replace(/\/$/, '')}/${path}`
  }, '')

  return expandPageParams(pattern, last(matches)?.params ?? {}) || '/'
}

/** Module-level so a re-created router does not re-count the page it starts on. */
let lastTrackedPathname: string | null = null

const trackPageView = ({ location, matches }: RouterState): void => {
  // Search-param / hash changes and non-navigation state updates (fetchers,
  // revalidation) keep the pathname, so they are not new page views. A
  // trailing slash (`/home/` vs `/home`) is the same page too.
  const pathname = location.pathname.replace(/(.)\/+$/, '$1')
  if (pathname === lastTrackedPathname) return
  lastTrackedPathname = pathname

  trackLogRocketEvent(PAGE_VIEWED_EVENT, { route: getRoutePattern(matches) })
}

/**
 * Tracks the router's current page, then every committed navigation to a new
 * pathname. The current page is tracked again whenever the LogRocket session is
 * reset (logout), so the new session does not start without a page view.
 * Returns the unsubscribe function. No-op when LogRocket is disabled.
 */
export const subscribeLogRocketPageViews = (router: DataRouter): (() => void) => {
  if (!isLogRocketEnabled()) return () => {}

  trackPageView(router.state)
  const unsubscribeRouter = router.subscribe(trackPageView)
  const unsubscribeSessionReset = onLogRocketSessionReset(() => {
    lastTrackedPathname = null
    trackPageView(router.state)
  })

  return () => {
    unsubscribeRouter()
    unsubscribeSessionReset()
  }
}
